import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  ReactNode
} from 'react';

type ResizablePanelProps = {
  defaultWidth?: number;
  minWidth?: number;
  maxFractionOfPageWidth?: number;
  children: ReactNode;
};

export const ResizablePanel = ({
  defaultWidth = 470,
  minWidth = 350,
  maxFractionOfPageWidth = 0.3,
  children
}: ResizablePanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const clamp = useCallback(
    (value: number): number => {
      const maxWidth =
        document.documentElement.clientWidth * maxFractionOfPageWidth;
      return Math.max(minWidth, Math.min(maxWidth, value));
    },
    [minWidth, maxFractionOfPageWidth]
  );

  const updateWidthFromClientX = useCallback(
    (clientX: number) => {
      const panel = panelRef.current;
      if (!panel) return;

      const delta = dragStartXRef.current - clientX;
      const newWidth = clamp(dragStartWidthRef.current + delta);
      panel.style.width = newWidth + 'px';
    },
    [clamp]
  );

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!draggingRef.current) return;
      if (e.cancelable) e.preventDefault();
      const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
      updateWidthFromClientX(x);
    },
    [updateWidthFromClientX]
  );

  const handleDragEnd = useCallback(() => {
    draggingRef.current = false;
    setIsDragging(false);

    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
    window.removeEventListener('touchmove', handleDragMove);
    window.removeEventListener('touchend', handleDragEnd);
  }, [handleDragMove]);

  const handleDragStart = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const panel = panelRef.current;
      if (!panel) return;

      const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
      dragStartXRef.current = x;
      dragStartWidthRef.current = panel.getBoundingClientRect().width;

      draggingRef.current = true;
      setIsDragging(true);

      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    },
    [handleDragMove, handleDragEnd]
  );

  useEffect(() => {
    const gutter = gutterRef.current;
    if (!gutter) return;

    const handleMouseDown = (e: MouseEvent) => handleDragStart(e);
    const handleTouchStart = (e: TouchEvent) => handleDragStart(e);

    gutter.addEventListener('mousedown', handleMouseDown);
    gutter.addEventListener('touchstart', handleTouchStart, { passive: false });

    return () => {
      gutter.removeEventListener('mousedown', handleMouseDown);
      gutter.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [handleDragStart, handleDragMove, handleDragEnd]);

  return (
    <div
      className={
        'resizable-panel' + (isDragging ? ' resizable-panel--dragging' : '')
      }
    >
      <div
        ref={gutterRef}
        className="resizable-panel__gutter"
        role="separator"
        aria-orientation="vertical"
        data-testid="resizable-panel-resizer"
      >
        <div className="resizable-panel__gutter-icon">
          <i className="fa-light fa-arrows-left-right"></i>
        </div>
      </div>
      <div
        ref={panelRef}
        className="resizable-panel__content"
        style={{ width: defaultWidth }}
      >
        {children}
      </div>
    </div>
  );
};
